import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Trash2Icon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H3, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser, userCache } from "utils/currentUser";
import util from "utils/utils";

import type {
  IpAllowListEntry,
  SettingsOwnerOrganisation,
  ValidateIpActionRequest,
} from "../../types";

type IpRestrictionProps = { userIp: string };

const IpRestriction = ({ userIp }: IpRestrictionProps) => {
  const { t } = useTranslation();
  const user = useCurrentUser();

  const ownerOrganisation = user?.ownerOrganisation as
    | SettingsOwnerOrganisation
    | undefined;

  const [newIpModalOpen, setNewIpModalOpen] = useState(false);
  const [newAddress, setNewAddress] = useState<IpAllowListEntry | null>(null);

  const updateIpRestriction = useCallback(
    (enabled?: boolean) => {
      if (!ownerOrganisation?._id) return;
      const ipRestriction = { ...ownerOrganisation.ipRestriction };
      ipRestriction.isEnabled = enabled;
      if (enabled) {
        util
          .confirm(
            "Enable IP address restriction?",
            `This will prevent access to ${util.appName()} for any users outside of the IP ranges specified.`,
          )
          .then(() => {
            api.organisations.updateSettings(
              ownerOrganisation._id,
              { ipRestriction },
              () => {
                userCache.updateOrganisation("ipRestriction", ipRestriction);
                toast.success(t("generic.typeUpdated", { type: "Settings" }));
              },
              () => {},
            );
          })
          .catch(() => {});
      } else {
        api.organisations.updateSettings(
          ownerOrganisation._id,
          { ipRestriction },
          () => {
            userCache.updateOrganisation("ipRestriction", ipRestriction);
            toast.success(t("generic.typeUpdated", { type: "Settings" }));
          },
          () => {},
        );
      }
    },
    [t, ownerOrganisation],
  );

  const deleteIpAddress = useCallback(
    (ip: IpAllowListEntry) => {
      if (!ownerOrganisation?._id) return;
      const ipRestriction = { ...ownerOrganisation.ipRestriction };
      const allowList = [...(ipRestriction.allowList || [])];
      const index = allowList.findIndex(
        (entry) => entry.address === ip.address,
      );
      if (index > -1) {
        allowList.splice(index, 1);
      }
      ipRestriction.allowList = allowList;
      if (allowList.length === 0) {
        ipRestriction.isEnabled = false;
      }
      api.organisations.updateSettings(
        ownerOrganisation._id,
        { ipRestriction },
        () => {
          userCache.updateOrganisation("ipRestriction", ipRestriction);
          toast.success("IP address removed");
        },
        () => {},
      );
    },
    [ownerOrganisation],
  );

  const addIpAddress = useCallback(() => {
    if (!ownerOrganisation?._id || !newAddress) return;
    const ipRestriction = { ...ownerOrganisation.ipRestriction };
    const allowList = [...(ipRestriction?.allowList || [])];
    allowList.push(newAddress);
    ipRestriction.allowList = allowList;
    api.organisations.updateSettings(
      ownerOrganisation._id,
      { ipRestriction },
      () => {
        userCache.updateOrganisation("ipRestriction", ipRestriction);
        toast.success("IP address added");
        setNewIpModalOpen(false);
        setNewAddress(null);
      },
      () => {},
    );
  }, [ownerOrganisation, newAddress]);

  const checkValidIpAction = useCallback(
    (
      ip: IpAllowListEntry | null,
      action: "validateIpAddress" | "validateEnableIpRestriction",
      callback: (confirmed?: boolean) => void,
    ) => {
      if (!ownerOrganisation?._id) return;
      api.organisations.checkValidIpAction(
        ownerOrganisation._id,
        { ip, action } as ValidateIpActionRequest,
        (data) => {
          if (data.success) callback(true);
          else if (data.failType === "addressOutOfRange") {
            util
              .confirm(
                "Add to allow list?",
                "The address you have entered does not match your current IP address, are you sure you want to add this.",
              )
              .then(() => {
                callback();
              })
              .catch(() => {});
          } else if (data.failType === "lockOutWarning") {
            util
              .confirm(
                "Enable IP address restriction?",
                "Your current IP address is outside of the IP ranges specified in the allow list, if you enable this setting your account will not be able to access the platform.",
              )
              .then(() => {
                callback(true);
              })
              .catch(() => {});
          }
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [ownerOrganisation],
  );

  if (!ownerOrganisation) {
    return null;
  }

  const ipRestrictionDisabled =
    (ownerOrganisation.ipRestriction?.allowList?.length ?? 0) === 0;
  const ipRestrictionEnabled = ownerOrganisation.ipRestriction?.isEnabled;

  return (
    <>
      <Card fluid>
        <CardContent>
          <H3>
            IP address restriction
            <Subheader>
              Restrict access to {util.appName()} by IP address.
              {userIp ? (
                <>
                  {" "}
                  Your current IP address is <b>{userIp}</b>.
                </>
              ) : null}
            </Subheader>
          </H3>
          <Checkbox
            toggle
            checked={ipRestrictionEnabled}
            onChange={(_e, c) =>
              ipRestrictionEnabled
                ? updateIpRestriction(c.checked)
                : checkValidIpAction(
                    null,
                    "validateEnableIpRestriction",
                    updateIpRestriction,
                  )
            }
            disabled={ipRestrictionDisabled}
            data-tooltip={
              ipRestrictionDisabled
                ? "Setting is disabled because no IP addresess have been added to the allow list"
                : null
            }
          >
            Enable IP restriction
          </Checkbox>
          <H5>
            Allowed IP addresses and CIDR blocks
            <Subheader>
              Only allow logins from IP addresses within the following list.
            </Subheader>
          </H5>
          <Button onClick={() => setNewIpModalOpen(true)} color="primary">
            Add IP address to allow list
          </Button>
          {(ownerOrganisation.ipRestriction?.allowList?.length ?? 0) > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>IP address or CIDR block</TableHeaderCell>
                  <TableHeaderCell>Description</TableHeaderCell>
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ownerOrganisation.ipRestriction?.allowList?.map((ip) => (
                  <TableRow key={ip.address}>
                    <TableCell>{ip.address}</TableCell>
                    <TableCell>{ip.description}</TableCell>
                    <TableCell collapsing textAlign="right">
                      <Button
                        size="small"
                        onClick={() => deleteIpAddress(ip as IpAllowListEntry)}
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal open={newIpModalOpen}>
        <ModalHeader>Add allowed address</ModalHeader>
        <ModalContent>
          <p>
            Add an allowed address using the CIDR notation or as a single IP
            address.
          </p>
          <FormField>
            <FormLabel htmlFor="ipAddress">IP address or CIDR block</FormLabel>
            <Input
              id="ipAddress"
              fluid
              value={newAddress?.address ?? ""}
              placeholder="0.0.0.0/0"
              onChange={(e) => {
                setNewAddress((prevState) => ({
                  address: e.target.value,
                  description: prevState?.description,
                }));
              }}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="ipDescription">Description</FormLabel>
            <Input
              id="ipDescription"
              fluid
              value={newAddress?.description ?? ""}
              placeholder="Optional comment describing the entry"
              onChange={(e) => {
                setNewAddress((prevState) => ({
                  address: prevState?.address ?? "",
                  description: e.target.value,
                }));
              }}
            />
          </FormField>
        </ModalContent>
        <ModalActions>
          <Button
            color="grey"
            size="small"
            onClick={() => setNewIpModalOpen(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={() =>
              checkValidIpAction(newAddress, "validateIpAddress", addIpAddress)
            }
            color="secondary"
          >
            Confirm
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default IpRestriction;
